// The library page's queries beside its pages (`platform/media/library-pages.ts`): the lists'
// one refresh, and the poll of the files being indexed. The poll of a saving file is
// `platform/media/saving-poll.ts`.
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";

import { getAsset, getListAssetsQueryKey } from "../../api/generated/endpoints/assets/assets";
import type { Asset, AssetPage, IndexStatus } from "../../api/generated/model";
import { refreshLibraryLists } from "../../api/library-lists";

/** A file being indexed for search (spec S3) is read again this often. */
export const INDEXING_POLL_MS = 10_000;
/** At most this many at a time, the first the list shows: the others follow as these finish. */
export const INDEXING_POLLED = 10;

/** Reads every list of the brand's library again: the library, the trash, every filter. */
export function useRefreshLibrary(teamId: string, brandId: string) {
  const queryClient = useQueryClient();
  return useCallback(() => refreshLibraryLists(queryClient, teamId, brandId), [queryClient, teamId, brandId]);
}

/** The pages of every cached list with each read file's index state written in. A plain
 *  `AssetPage` (not pages) and an unchanged file are left as they are. */
export function withIndexStates(data: unknown, states: ReadonlyMap<string, IndexStatus | null>): unknown {
  if (typeof data !== "object" || data === null || !("pages" in data)) return data;
  const pages = (data as { pages: AssetPage[] }).pages;
  const changed = (asset: Asset) => states.has(asset.id) && states.get(asset.id) !== asset.index_status;
  if (!pages.some((page) => page.items.some(changed))) return data;
  return {
    ...data,
    pages: pages.map((page) => ({
      ...page,
      items: page.items.map((asset) =>
        changed(asset) ? { ...asset, index_status: states.get(asset.id) ?? null } : asset,
      ),
    })),
  };
}

/**
 * Ours (spec S3; live shows no index state): each file being indexed is read again every 10 s,
 * alone, and its state written into the cached pages, so a card's "Indexing for search" changes as
 * the worker answers. The list itself is not read again: that re-read every loaded page and, with
 * its new signatures, every picture (#794).
 */
export function useIndexingPoll(teamId: string, brandId: string, pending: readonly string[]) {
  const queryClient = useQueryClient();
  const polled = pending.slice(0, INDEXING_POLLED).join(",");
  useEffect(() => {
    if (polled === "") return;
    const ids = polled.split(",");
    const timer = window.setInterval(() => {
      void Promise.all(ids.map((id) => getAsset(teamId, brandId, id).catch(() => null))).then((read) => {
        const states = new Map(read.flatMap((asset) => (asset === null ? [] : [[asset.id, asset.index_status] as const])));
        queryClient.setQueriesData({ queryKey: [getListAssetsQueryKey(teamId, brandId)[0]] }, (data: unknown) =>
          withIndexStates(data, states),
        );
      });
    }, INDEXING_POLL_MS);
    return () => window.clearInterval(timer);
  }, [polled, teamId, brandId, queryClient]);
}
